import { useApolloClient, useMutation } from '@apollo/client';
import { Button, message } from 'antd';
import { motion } from 'framer-motion';
import { toPng } from 'html-to-image';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWindowSize } from 'react-use';
import { AppContext } from '../../AppContext';
import { CategoryEnum, S3PathEnum } from '../../__generated__/graphql';
import Dialer from '../../assets/images/dialer.png';
import PlaceholderImage from '../../assets/images/image-placeholder.png';
import Arrow from '../../assets/images/pointers.png';
import { DIRECTIONS, ROUTES } from '../../common/constants';
import { fileUpload, isTabletBrowser } from '../../common/utils';
import Sidebar from '../../components/Sidebar';
import useRouter from '../../hooks/useRouter';
import { AppActionType, AppContextType } from '../../types/appContext.type';
import { FloorPlanData } from '../../types/dashboard.type';
import { UPDATE_FLOOR_PLAN } from './graphql/mutations';
import { GET_IMAGE_SIGNED_URL } from './graphql/queries';
import './styles/selectDirection.less';

const normalizeAngle = (angle: number) => ((angle % 360) + 360) % 360;

const calculateShortestPath = (currentAngle: number, targetAngle: number) => {
  const normalizedCurrent = normalizeAngle(currentAngle);
  const normalizedTarget = normalizeAngle(targetAngle);

  const clockwiseDelta = (normalizedTarget - normalizedCurrent + 360) % 360;
  const antiClockwiseDelta = (normalizedCurrent - normalizedTarget + 360) % 360;

  return clockwiseDelta <= antiClockwiseDelta
    ? clockwiseDelta
    : -antiClockwiseDelta;
};

const SelectNorthDirection = () => {
  const {
    state: { floorPlanSequence },
    dispatch,
  } = useContext(AppContext) as AppContextType;
  const [arrowAngle, setArrowAngle] = useState<number>(0);
  const [image, setImage] = useState<string>();
  const [floorPlanId, setFloorPlanId] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const compassRef = useRef<HTMLDivElement>(null);
  const currentAngleRef = useRef<number>(0);
  const { width } = useWindowSize();
  const isTablet = isTabletBrowser(width);

  const client = useApolloClient();
  const { navigate } = useRouter();
  const [updateFloorPlan] = useMutation(UPDATE_FLOOR_PLAN);

  useEffect(() => {
    if (floorPlanSequence) {
      if ((floorPlanSequence.assets?.length ?? 0) > 0) {
        const angle =
          floorPlanSequence?.assets?.find(
            (asset) => asset?.category === CategoryEnum.NorthDirected,
          )?.angle || 0;
        setArrowAngle(angle);
      } else {
        setArrowAngle(0);
      }
      const baseImage =
        floorPlanSequence?.assets?.find(
          (asset) => asset?.category === CategoryEnum.Base,
        )?.key || PlaceholderImage;
      setImage(baseImage);
      setFloorPlanId(floorPlanSequence.id || '');
    }
  }, [floorPlanSequence]);

  const handleDirectionClick = useCallback((angle: number) => {
    const targetAngle = normalizeAngle(angle);
    const shortestPath = calculateShortestPath(
      currentAngleRef.current,
      targetAngle,
    );
    currentAngleRef.current = normalizeAngle(
      currentAngleRef.current + shortestPath,
    );
    setArrowAngle(currentAngleRef.current);
  }, []);

  const handleContinue = useCallback(async () => {
    if (!compassRef.current) {
      message.error('Compass container is not available.');
      return;
    }
    if (!floorPlanId) {
      message.error('Something went wrong. Please try again.');
      return;
    }

    try {
      setIsUploading(true);
      const dataUrl = await toPng(compassRef.current);
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'screenshot.png', { type: 'image/png' });

      const { data: signedUrlData } = await client.query({
        query: GET_IMAGE_SIGNED_URL,
        variables: {
          data: {
            fileName: file.name,
            s3Path: S3PathEnum.FloorPlanNorthDirectedImage,
          },
        },
        fetchPolicy: 'network-only',
      });

      const signedUrl = signedUrlData?.getImageSignedUrlWeb?.signedUrl;
      const signedUrlKey = signedUrlData?.getImageSignedUrlWeb?.key;

      if (!signedUrl) {
        throw new Error('Signed URL is missing or undefined.');
      }

      await fileUpload(signedUrl, file);

      const { data } = await updateFloorPlan({
        variables: {
          data: {
            floorPlanId,
            northDirectedImage: signedUrlKey,
            angle: arrowAngle,
          },
        },
      });

      if (data?.updateFloorPlanWeb?.data) {
        const assets = data?.updateFloorPlanWeb?.data?.assets || [];

        const angle =
          assets?.find((asset) => asset?.angle !== null)?.angle || 0;
        const updatedFloorPlan = {
          ...floorPlanSequence,
          assets: (data?.updateFloorPlanWeb?.data?.assets || []).map((asset) =>
            asset?.category === CategoryEnum.NorthDirected
              ? { ...asset, angle }
              : asset,
          ),
        };
        dispatch({
          type: AppActionType.setFloorPlanSequence,
          data: updatedFloorPlan as FloorPlanData,
        });

        navigate(ROUTES.CHOOSE_ENTRANCE);
      }
    } catch (error) {
      message.error('An error occurred. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [
    arrowAngle,
    client,
    dispatch,
    floorPlanId,
    floorPlanSequence,
    navigate,
    updateFloorPlan,
  ]);

  const renderCompassCircle = useMemo(
    () => (
      <div className={`compass-circle${isTablet ? '-mobile' : '-desktop'}`}>
        {DIRECTIONS.map((direction, index) => {
          const radian = ((360 - direction.angle) * Math.PI) / 180;
          const x1 = 150 + 80 * Math.cos(radian);
          const y1 = 150 + 80 * Math.sin(radian);
          const x2 = 150 + 50 * Math.cos(radian);
          const y2 = 150 + 50 * Math.sin(radian);
          const centerX = (x1 + x2) / 2;
          const centerY = (y1 + y2) / 2;

          return (
            <svg key={index}>
              <circle
                cx={centerX}
                cy={centerY}
                r={20}
                fill="transparent"
                pointerEvents="visible"
                onClick={() => handleDirectionClick(direction.angle)}
              />
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                strokeWidth={2}
                onClick={() => handleDirectionClick(direction.angle)}
              />
            </svg>
          );
        })}
        <motion.div
          className="motion-div"
          animate={{ rotate: -arrowAngle + 90 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <img src={Dialer} alt="Dialer" className="arrow" />
        </motion.div>
      </div>
    ),
    [arrowAngle, handleDirectionClick, isTablet],
  );

  return (
    <>
      {isTablet && (
        <div className="compass-text-mobile body-base semibold">
          Match the arrow on the dial with <span>"North"</span> on your image.
        </div>
      )}
      <div className={`floor-plan-${isTablet ? 'mobile' : 'desktop'}`}>
        <div ref={compassRef} className="plan-image-div">
          <img src={image} alt="Floor Plan" className="floor-plan-image" />
          <div className="arrow-overlay">
            <img
              src={Arrow}
              alt="Arrow"
              className="arrow"
              style={{ transform: `rotate(${-arrowAngle + 90}deg)` }}
            />
          </div>
        </div>
      </div>
      {isTablet && (
        <div className="compass-circle-mobile">{renderCompassCircle}</div>
      )}
      <Sidebar
        loading={isUploading}
        onSave={handleContinue}
        headerContent={
          <div className="body-base semibold compass-text-desktop">
            Match the arrow on the dial with <span>"North"</span> on your image.
          </div>
        }
        bodyContent={renderCompassCircle}
      />

      {isTablet && (
        <div className="button-div">
          <Button
            type="primary"
            htmlType="submit"
            className="continue-button"
            onClick={handleContinue}
            loading={isUploading}
          >
            Continue
          </Button>
        </div>
      )}
    </>
  );
};

export default SelectNorthDirection;
